import React, { FC, useMemo, useState } from 'react';
import styled from 'styled-components';
import SubNavigationLayout from '../../shared/navigation/SubNavigationLayout';
import { LinkContainer } from '../../shared/navigation/NavigationLinksContainer';
import { THEME_MUTED, THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import { NavigationLink } from '../../shared/navigation/NavigationLink';
import { useEnvironment } from '../../shared/components/EnvironmentContext';
import { ENVIRONMENT_DEMO } from '../../constants';
import { ProductGroupDetailNavigation } from './ProductGroupDetailNavigation';
import { useLibraryManagedScopes } from '../marketing/productgroups/api/useLibraryManagedProductGroupsFeaturePages';
import { useTranslation } from 'react-i18next';
import { mapLoanFormat } from '../../../models/LoanFormat';
import { DropDownMenu } from '../../shared/common/DropDownMenu';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { useMatch, useSearchParams } from 'react-router-dom';

const StDropdownWrapper = styled.div`
    display: flex;
`;

const StDropdown = styled(DropDownMenu)`
    margin-right: 10px;
    && a {
        display: flex;
        font-size: 18px;
    }

    && ul > li > a {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0 15px;
        & span {
            flex-grow: 1;
        }

        & i:last-of-type {
            position: relative !important;
        }
    }
`;

const StDropdownContainer = styled.div`
    .scope-dropdown__single-value .lm-active-count {
        display: none;
    }
`;

const StLabelContainer = styled.div`
    display: flex;
    align-items: center;
`;

const StActiveCount = styled.span`
    color: ${themeColor(THEME_MUTED)};
    font-size: 14px;
`;

const StLink = styled.a<{ isActive: boolean }>`
    color: ${props => props.isActive && themeColor(THEME_PRIMARY)} !important;
    white-space: nowrap !important;
    font-size: 18px !important;
    padding: 8px !important;
`;

type Props = {
    readonly isSticky: boolean;
};

const MerchandisingNavigation: FC<Props> = ({ isSticky }) => {
    const [isScopeExpanded, setIsScopeExpanded] = useState(false);
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const { spoc } = useAuthentication();

    const libraryManagedScopes = useLibraryManagedScopes();
    const scopeOptions = useMemo(
        () =>
            libraryManagedScopes.map(scope => {
                const loanFormatValue = mapLoanFormat(scope.loanFormat)?.toLowerCase();

                return {
                    value: scope.scopeId,
                    label: (
                        <StLabelContainer>
                            <span>
                                {t(`components.productGroupScopeDropdown.allAges.${loanFormatValue}`, {
                                    defaultValue: t('components.productGroupScopeDropdown.discover')
                                })}
                            </span>
                            <StActiveCount className="lm-active-count">
                                &nbsp;({scope.activeProductGroupCount}/{scope.totalProductGroupCount})
                            </StActiveCount>
                        </StLabelContainer>
                    )
                };
            }),
        [libraryManagedScopes, t]
    );

    // CatMan is not available in the demo environment
    const libraryManagedProductGroupsEnabled = useEnvironment() !== ENVIRONMENT_DEMO;

    const historyScopeId = searchParams.get('lib-scope');
    const historyScopeIdInt = !historyScopeId ? 1 : parseInt(historyScopeId);

    const selectedScope = libraryManagedScopes.find(scope => {
        return scope.scopeId === historyScopeIdInt;
    });
    const scopeContext = mapLoanFormat(selectedScope?.loanFormat)?.toLowerCase();

    const productGroupsMatch = useMatch('/merchandising/productgroups');
    const productGroupDetailMatch = useMatch('/merchandising/productgroups/:id');

    return (
        <SubNavigationLayout
            left={
                productGroupDetailMatch ? (
                    <ProductGroupDetailNavigation id={productGroupDetailMatch.params.id ?? ''} />
                ) : productGroupsMatch ? (
                    <StDropdownWrapper>
                        {spoc.id?.startsWith('SITE') ? (
                            <StDropdownContainer>
                                <StDropdown
                                    align="left"
                                    gap={2}
                                    expanded={isScopeExpanded}
                                    onToggleClick={() => setIsScopeExpanded(!isScopeExpanded)}
                                    onClickOutside={() => setIsScopeExpanded(false)}
                                    label={t(`components.productGroupScopeDropdown.allAges.${scopeContext}`, {
                                        defaultValue: t('components.productGroupScopeDropdown.discover')
                                    })}
                                >
                                    {scopeOptions.map(scopeOption => {
                                        const isActive = scopeOption.value === historyScopeIdInt;
                                        return (
                                            <li key={scopeOption.value}>
                                                <StLink
                                                    isActive={isActive}
                                                    className={isActive ? 'checked' : undefined}
                                                    onClick={() => {
                                                        setSearchParams(
                                                            { 'lib-scope': scopeOption.value.toString() },
                                                            { replace: true }
                                                        );
                                                        setIsScopeExpanded(false);
                                                    }}
                                                >
                                                    {scopeOption.label}
                                                    {isActive && <i className="material-icons">check</i>}
                                                </StLink>
                                            </li>
                                        );
                                    })}
                                </StDropdown>
                            </StDropdownContainer>
                        ) : null}
                    </StDropdownWrapper>
                ) : null
            }
            center={
                !isSticky ? (
                    <LinkContainer>
                        {libraryManagedProductGroupsEnabled ? (
                            <li>
                                <NavigationLink to="/merchandising/productgroups">
                                    <span>{t('menu.yourBorrowbox.merchandising.productGroups')}</span>
                                </NavigationLink>
                            </li>
                        ) : null}
                    </LinkContainer>
                ) : null
            }
            isSticky={isSticky}
            width="30"
        />
    );
};

export default MerchandisingNavigation;
